import React, { useEffect, useState } from "react";
import { PageHeader, Modal, StatusBadge, Toast, EmptyState } from "@/components/Layout";
import api, { soft } from "@/lib/api";
import Select from "@/components/Select";
import { useAuth } from "@/context/AuthContext";
import { lock } from "@/lib/perm";
import ViewOnly from "@/components/ViewOnly";

export default function Campaigns() {
  const { has } = useAuth();
  const [rows, setRows] = useState([]);
  const [units, setUnits] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState({text:"",type:"info"});

  const load = async () => {
    const [c,u,cf] = await Promise.all([api.get("/planco/campaigns"), soft(api.get("/mdm/org-units"), []), soft(api.get("/config"), null)]);
    // Sortez: active/draft primele, apoi closed/archived la final (după anul descrescător)
    const statusRank = { active: 0, draft: 1, closed: 2, archived: 3 };
    const sorted = [...c.data].sort((a,b) => {
      const ra = statusRank[a.status] ?? 9;
      const rb = statusRank[b.status] ?? 9;
      if (ra !== rb) return ra - rb;
      return (b.planning_year || 0) - (a.planning_year || 0);
    });
    setRows(sorted); setUnits(u.data); setCfg(cf.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) await api.put(`/planco/campaigns/${editing.id}`, editing);
      else await api.post("/planco/campaigns", editing);
      setEditing(null); load(); setMsg({text:"Salvat",type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };
  const action = async (id, act) => {
    try { await api.post(`/planco/campaigns/${id}/${act}`); load();
      setMsg({text:`Operație "${act}" reușită`,type:"success"}); setTimeout(()=>setMsg({text:""}),1500);
    } catch(e){ setMsg({text:e?.response?.data?.detail||"Eroare",type:"error"}); }
  };

  const newCampaign = () => {
    const y = cfg?.planning_year || (new Date().getFullYear() + 1);
    setEditing({ name: `Campanie ${y}`, planning_year: y,
      allowed_periods: [], personnel_categories: [], scope_org_unit_ids: [], max_simultaneous_absences: 0,
      access_start: `${y-1}-11-01`, access_end: `${y-1}-12-31`,
      approval_co_start: `${y-1}-12-01`, approval_co_end: `${y-1}-12-31`,
      notes: "" });
  };

  const toggleCat = (cat) => {
    const cur = editing.personnel_categories || [];
    setEditing({...editing, personnel_categories: cur.includes(cat) ? cur.filter(x=>x!==cat) : [...cur, cat]});
  };
  // Categoriile disponibile la perioadele permise: cele ale campaniei (dacă are listă), altfel toate
  const campCats = () => (editing.personnel_categories?.length ? editing.personnel_categories : (cfg?.personnel_categories||["TESA"]));
  const addAllowed = () => setEditing({...editing, allowed_periods: [...(editing.allowed_periods||[]), {personnel_category: campCats()[0] || "TESA", start_date:"", end_date:"", label:""}]});
  const updAllowed = (i, k, v) => { const a = [...editing.allowed_periods]; a[i] = {...a[i], [k]: v}; setEditing({...editing, allowed_periods: a}); };
  const rmAllowed = (i) => { const a = [...editing.allowed_periods]; a.splice(i,1); setEditing({...editing, allowed_periods: a}); };

  return (
    <>
      <PageHeader title="Campanii de Programare" subtitle={`${rows.length} campanii`}
        actions={<button className="btn btn-primary" onClick={newCampaign} data-testid="add-campaign" {...lock(has("campaigns.create"))}>+ Campanie nouă</button>} />
      <Toast msg={msg.text} type={msg.type} />
      <div style={{display:"grid",gap:12}}>
        {rows.map(c => (
          <div key={c.id} className="card fade-up" style={{padding:20,
              borderLeft: c.status==="active" ? "3px solid var(--blue-primary)" : "1px solid #E2E8F0"}}
              data-testid={`campaign-${c.planning_year}`}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
              <div style={{flex:1}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{fontSize:11,color:"var(--blue-primary)",fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase"}}>An {c.planning_year}</div>
                  <StatusBadge status={c.status} />
                </div>
                <div style={{fontSize:17,fontWeight:700,color:"var(--navy)",marginTop:4}}>{c.name}</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginTop:12,fontSize:13,color:"#334155"}}>
                  <div><b>Valabilitate programare:</b> {c.access_start} → {c.access_end}</div>
                  <div><b>Aprobare programare CO:</b> {c.approval_co_start} → {c.approval_co_end}</div>
                </div>
                <div style={{marginTop:10,fontSize:12,color:"#334155"}}>
                  <b>Categorii de personal:</b> {(c.personnel_categories||[]).length ? c.personnel_categories.join(", ") : "toate"}
                </div>
                {(c.allowed_periods||[]).length > 0 && (
                  <div style={{marginTop:10,fontSize:12,color:"#334155"}}>
                    <b>Perioade permise:</b> {c.allowed_periods.map(a => `${a.personnel_category}: ${a.start_date}→${a.end_date}${a.label?` (${a.label})`:""}`).join(" · ")}
                  </div>
                )}
                {c.notes && <div style={{marginTop:10,fontSize:12,color:"#64748B",fontStyle:"italic"}}>{c.notes}</div>}
              </div>
              <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
                {c.status==="draft" && <button className="btn btn-primary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>action(c.id,"activate")} data-testid={`activate-${c.planning_year}`} {...lock(has("campaigns.activate"))}>Activează</button>}
                {c.status==="active" && <button className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>action(c.id,"close")} {...lock(has("campaigns.close"))}>Închide</button>}
                {c.status==="closed" && <button className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>action(c.id,"reopen")} {...lock(has("campaigns.reopen"))}>Redeschide</button>}
                {c.status==="closed" && <button className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>action(c.id,"archive")} {...lock(has("campaigns.archive"))}>Arhivează</button>}
                <button className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>setEditing({...c, view:true})} data-testid={`view-campaign-${c.planning_year}`}>Vizualizează</button>
                {(c.status==="draft" || c.status==="active") && <button className="btn btn-secondary" style={{padding:"4px 12px",fontSize:12}} onClick={()=>setEditing({...c})} {...lock(has("campaigns.update"))}>Editează</button>}
              </div>
            </div>
          </div>
        ))}
        {rows.length===0 && <EmptyState text='Nicio campanie. Apasă „+ Campanie nouă" pentru a începe.' />}
      </div>

      {editing && (
        <Modal title={editing.view?"Campanie — vizualizare":editing.id?"Editează campanie":"Campanie nouă"} onClose={()=>setEditing(null)} width={820}>
          <ViewOnly on={editing.view}>
          <div className="grid-2">
            <div style={{gridColumn:"1/-1"}}><label className="label label-req">Denumire campanie</label>
              <input className="input" value={editing.name} onChange={e=>setEditing({...editing,name:e.target.value})} data-testid="camp-name" /></div>
            <div><label className="label label-req">Anul de programare</label>
              <input className="input" type="number" value={editing.planning_year} onChange={e=>setEditing({...editing,planning_year:parseInt(e.target.value)||editing.planning_year})} data-testid="camp-year" /></div>
            <div><label className="label">Max absențe simultane / unitate (0=nelimitat)</label>
              <input className="input" type="number" value={editing.max_simultaneous_absences||0} onChange={e=>setEditing({...editing,max_simultaneous_absences:parseInt(e.target.value)||0})} /></div>
            <div><label className="label label-req">Valabilitate programare — început</label>
              <input className="input" type="date" value={editing.access_start} onChange={e=>setEditing({...editing,access_start:e.target.value})} data-testid="camp-access-start" /></div>
            <div><label className="label label-req">Valabilitate programare — sfârșit</label>
              <input className="input" type="date" value={editing.access_end} onChange={e=>setEditing({...editing,access_end:e.target.value})} data-testid="camp-access-end" /></div>
            <div><label className="label label-req">Aprobare programare CO — început</label>
              <input className="input" type="date" value={editing.approval_co_start} onChange={e=>setEditing({...editing,approval_co_start:e.target.value})} /></div>
            <div><label className="label label-req">Aprobare programare CO — sfârșit</label>
              <input className="input" type="date" value={editing.approval_co_end} onChange={e=>setEditing({...editing,approval_co_end:e.target.value})} /></div>
          </div>

          <div style={{marginTop:16}} data-testid="camp-categories">
            <label className="label">Categorii de personal incluse în campanie (nimic bifat = toate)</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,border:"1px solid #E2E8F0",borderRadius:6,padding:10}}>
              {(cfg?.personnel_categories||[]).map(cat => {
                const on = (editing.personnel_categories||[]).includes(cat);
                return <label key={cat} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12,padding:"3px 8px",background:on?"var(--tint-100)":"#F8FAFC",borderRadius:4,cursor:"pointer"}}>
                  <input type="checkbox" checked={on} onChange={()=>toggleCat(cat)} /> {cat}
                </label>;
              })}
            </div>
            <div style={{fontSize:11,color:"#64748B",marginTop:4}}>Într-un an poți avea mai multe campanii, atâta timp cât nu includ aceleași categorii pe aceleași structuri. Fiecare raport de muncă programează în campania categoriei lui.</div>
          </div>

          {/* Perioade permise per categorie */}
          <div style={{marginTop:16}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <label className="label" style={{margin:0}}>Perioade permise (pentru anul {editing.planning_year}) — pe categorie personal</label>
              <button className="btn btn-secondary" style={{padding:"3px 10px",fontSize:12}} onClick={addAllowed}>+ Adaugă perioadă</button>
            </div>
            <div style={{fontSize:11,color:"#64748B",marginBottom:8}}>Ex: personalul didactic doar în vacanțe universitare. Gol = fără restricție per categorie.</div>
            <div style={{display:"grid",gap:6,maxHeight:200,overflow:"auto"}}>
              {(editing.allowed_periods||[]).map((ap,i)=>(
                <div key={i} style={{display:"grid",gridTemplateColumns:"1.5fr 1fr 1fr 1.5fr auto",gap:6}}>
                  <Select className="select" value={ap.personnel_category} onChange={e=>updAllowed(i,"personnel_category",e.target.value)}>
                    {Array.from(new Set([...campCats(), ap.personnel_category])).map(c=><option key={c}>{c}</option>)}
                  </Select>
                  <input className="input" type="date" value={ap.start_date} onChange={e=>updAllowed(i,"start_date",e.target.value)} />
                  <input className="input" type="date" value={ap.end_date} onChange={e=>updAllowed(i,"end_date",e.target.value)} />
                  <input className="input" placeholder="Etichetă (ex. Vacanță semestru I)" value={ap.label||""} onChange={e=>updAllowed(i,"label",e.target.value)} />
                  <button className="btn btn-danger" style={{padding:"3px 10px",fontSize:12}} onClick={()=>rmAllowed(i)}>×</button>
                </div>
              ))}
            </div>
          </div>

          <div style={{marginTop:16}}>
            <label className="label">Structuri incluse (gol = toate)</label>
            <div style={{display:"flex",flexWrap:"wrap",gap:6,maxHeight:120,overflow:"auto",border:"1px solid #E2E8F0",borderRadius:6,padding:10}}>
              {units.map(u => {
                const on = (editing.scope_org_unit_ids||[]).includes(u.id);
                return <label key={u.id} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12,padding:"3px 8px",background:on?"var(--tint-100)":"#F8FAFC",borderRadius:4,cursor:"pointer"}}>
                  <input type="checkbox" checked={on} onChange={e=>{
                    const set = new Set(editing.scope_org_unit_ids||[]);
                    e.target.checked ? set.add(u.id) : set.delete(u.id);
                    setEditing({...editing, scope_org_unit_ids: Array.from(set)});
                  }} />
                  {u.name}
                </label>;
              })}
            </div>
          </div>

          <div style={{marginTop:14}}>
            <label className="label">Note</label>
            <textarea className="textarea" rows={2} value={editing.notes||""} onChange={e=>setEditing({...editing,notes:e.target.value})} />
          </div>

          </ViewOnly>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:18}}>
            <button className="btn btn-secondary" onClick={()=>setEditing(null)}>{editing.view?"Închide":"Anulează"}</button>
            {!editing.view && <button className="btn btn-primary" onClick={save} data-testid="save-campaign" {...lock(has(editing.id?"campaigns.update":"campaigns.create"))}>Salvează</button>}
          </div>
        </Modal>
      )}
    </>
  );
}
